import { useCallback, useEffect, useRef, useState } from "react";
import {
  type BrandImageMeta,
  brandImageContentUrl,
  clearCsrfToken,
  deleteBrandImage,
  getBrandImageMeta,
  putBrandImage,
} from "../../lib/brand-image-api";
import { TerritoryIdentityError } from "../../lib/territory-identity-api";
import { emitBrandImageMutation } from "../../lib/brand-image-invalidation";
import {
  brandImageErrorMessage,
  prevalidateBrandImage,
} from "../../lib/brand-image-validate";

/**
 * LOD0 Brand Image management state (Dashboard → Territory Identity).
 *
 * Same zero-stale-authority philosophy as useTerritoryIdentity: monotonic
 * request id + AbortController so a wallet-A / stale response can never become
 * current UI authority; local preview is temporary and released on any context
 * loss. SAVE is explicit — selecting a file NEVER uploads.
 */
export type BrandImageStatus =
  | "auth_required" // no active management authority (no identity / not authed)
  | "loading"
  | "no_image"
  | "existing"
  | "local_selection"
  | "saving"
  | "success"
  | "removed"
  | "error";

export interface BrandImageUIState {
  status: BrandImageStatus;
  meta: BrandImageMeta | null; // persisted image metadata (if any)
  previewUrl: string | null; // local, unsaved object URL (if a file is selected)
  errorCode: string | null;
  errorMessage: string | null;
}

export interface UseBrandImage extends BrandImageUIState {
  selectFile: (file: File) => Promise<void>;
  clearSelection: () => void;
  save: () => Promise<boolean>;
  remove: () => Promise<boolean>;
  reload: () => void;
  contentUrl: string | null; // cache-busted URL of the persisted image
}

async function decodeDimensions(file: File): Promise<{ width: number; height: number } | null> {
  try {
    // Browser path — precise decoded dimensions.
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(file);
      const dims = { width: bmp.width, height: bmp.height };
      bmp.close?.();
      return dims;
    }
  } catch {
    /* fall through — server remains authoritative */
  }
  return null;
}

export function useBrandImage(
  identityId: string | null,
  hasAuthority: boolean,
): UseBrandImage {
  const [meta, setMeta] = useState<BrandImageMeta | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<BrandImageStatus>("auth_required");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reqIdRef = useRef(0);
  const selectedFileRef = useRef<File | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const releasePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    selectedFileRef.current = null;
    setPreviewUrl(null);
  }, []);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // Any authority/context change: drop local preview + CSRF token so a stale
  // wallet-A selection or token can never persist under wallet B / new identity.
  useEffect(() => {
    releasePreview();
    setErrorCode(null);
    setErrorMessage(null);
    clearCsrfToken();
  }, [identityId, hasAuthority, releasePreview]);

  // Load persisted metadata for the current identity.
  useEffect(() => {
    const myId = ++reqIdRef.current;
    const controller = new AbortController();
    if (!hasAuthority || !identityId) {
      setStatus("auth_required");
      setMeta(null);
      return () => controller.abort();
    }
    setStatus("loading");
    (async () => {
      try {
        const m = await getBrandImageMeta(identityId, controller.signal);
        if (myId !== reqIdRef.current) return;
        setMeta(m);
        setStatus(m ? "existing" : "no_image");
      } catch (e) {
        if (controller.signal.aborted || myId !== reqIdRef.current) return;
        if (e instanceof TerritoryIdentityError && e.status === 401) {
          setStatus("auth_required");
        } else {
          setErrorCode("UNKNOWN");
          setErrorMessage(brandImageErrorMessage("UNKNOWN"));
          setStatus("error");
        }
      }
    })();
    return () => controller.abort();
  }, [identityId, hasAuthority, reloadTick]);

  // Release the object URL on unmount.
  useEffect(() => () => releasePreview(), [releasePreview]);

  const selectFile = useCallback(
    async (file: File) => {
      const dims = await decodeDimensions(file);
      const check = prevalidateBrandImage({
        size: file.size,
        type: file.type,
        width: dims?.width,
        height: dims?.height,
      });
      if (!check.ok) {
        releasePreview();
        setErrorCode(check.code ?? "UNKNOWN");
        setErrorMessage(check.message ?? brandImageErrorMessage(check.code ?? "UNKNOWN"));
        setStatus("error");
        return;
      }
      releasePreview();
      const url = URL.createObjectURL(file);
      previewUrlRef.current = url;
      selectedFileRef.current = file;
      setPreviewUrl(url);
      setErrorCode(null);
      setErrorMessage(null);
      setStatus("local_selection");
    },
    [releasePreview],
  );

  const clearSelection = useCallback(() => {
    releasePreview();
    setErrorCode(null);
    setErrorMessage(null);
    setStatus(meta ? "existing" : "no_image");
  }, [meta, releasePreview]);

  const save = useCallback(async (): Promise<boolean> => {
    const file = selectedFileRef.current;
    if (!identityId || !file) return false;
    const myId = reqIdRef.current;
    setStatus("saving");
    setErrorCode(null);
    setErrorMessage(null);
    try {
      const expected = meta ? meta.revision : 0;
      const buf = await file.arrayBuffer();
      const res = await putBrandImage(identityId, buf, file.type, expected);
      if (myId !== reqIdRef.current) return false; // context changed mid-flight
      releasePreview();
      setMeta(res.brand_image);
      setStatus("success");
      // Confirmed authoritative mutation → notify the live viewer to re-resolve
      // THIS identity (no reload). Only fires on success.
      emitBrandImageMutation(identityId, "save");
      return true;
    } catch (e) {
      if (myId !== reqIdRef.current) return false;
      const code = e instanceof TerritoryIdentityError ? e.code : "UNKNOWN";
      setErrorCode(code);
      setErrorMessage(brandImageErrorMessage(code));
      setStatus("error");
      // Concurrency / lifecycle drift → re-resolve authoritative metadata.
      if (code === "BRAND_IMAGE_VERSION_CONFLICT" || code === "BRAND_IMAGE_TERRITORY_INELIGIBLE") {
        reload();
      }
      return false;
    }
  }, [identityId, meta, releasePreview, reload]);

  const remove = useCallback(async (): Promise<boolean> => {
    if (!identityId || !meta) return false;
    const myId = reqIdRef.current;
    setStatus("saving");
    setErrorCode(null);
    setErrorMessage(null);
    try {
      await deleteBrandImage(identityId, meta.revision);
      if (myId !== reqIdRef.current) return false;
      releasePreview();
      setMeta(null);
      setStatus("removed");
      emitBrandImageMutation(identityId, "remove");
      return true;
    } catch (e) {
      if (myId !== reqIdRef.current) return false;
      const code = e instanceof TerritoryIdentityError ? e.code : "UNKNOWN";
      setErrorCode(code);
      setErrorMessage(brandImageErrorMessage(code));
      setStatus("error");
      if (code === "BRAND_IMAGE_VERSION_CONFLICT" || code === "BRAND_IMAGE_NOT_FOUND") {
        reload();
      }
      return false;
    }
  }, [identityId, meta, releasePreview, reload]);

  const contentUrl = meta ? brandImageContentUrl(meta.identity_id, meta.revision) : null;

  return {
    status,
    meta,
    previewUrl,
    errorCode,
    errorMessage,
    selectFile,
    clearSelection,
    save,
    remove,
    reload,
    contentUrl,
  };
}
